/**
 * Auth Context
 *
 * Provides authentication state to the app.
 * On mount, checks localStorage for session token set by the OAuth bridge page.
 * If found, validates via API. Falls back to device auth if no session.
 */

import { createContext, useState, useEffect, useCallback, useMemo } from 'react'
import type { AuthUser } from '@/lib/auth-client'
import { getCurrentUser, getStoredUser, navigateToGoogleLogin, logoutUser } from '@/lib/auth-client'

export interface AuthState {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
  login: () => void
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthState>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  login: () => {},
  logout: async () => {},
  refreshUser: async () => {},
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const refreshUser = useCallback(async () => {
    const currentUser = await getCurrentUser()
    setUser(currentUser)
    setIsLoading(false)
  }, [])

  useEffect(() => {
    // Check for cached user from OAuth bridge page immediately
    const cached = getStoredUser()
    if (cached) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUser(cached)
      setIsLoading(false)
    }
    // Then validate with server
    refreshUser()
  }, [refreshUser])

  const login = useCallback(() => {
    navigateToGoogleLogin()
  }, [])

  const logout = useCallback(async () => {
    await logoutUser()
    setUser(null)
  }, [])

  const value = useMemo(() => ({
    user,
    isLoading,
    isAuthenticated: user !== null,
    login,
    logout,
    refreshUser,
  }), [user, isLoading, login, logout, refreshUser])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
